/**
 * Answer scoring, including typo tolerance (LESSON-TYPES.md).
 *
 * The whole problem in one pair of examples from TESTING.md:
 *
 * - `hous` → `house` must be **accepted**. A child who typed that knows the word.
 * - `horse` → `house` must be **rejected**. That is a different animal.
 *
 * Both are edit distance 1, so distance alone cannot separate them. The rule that
 * can is about *how* the strings differ:
 *
 * > **Insertions, deletions and transpositions are typos. A substitution is only
 * > a typo if the two keys are next to each other on the keyboard.**
 *
 * Missing a letter, doubling one, or swapping two is a slip of the hand.
 * Deliberately typing a different letter usually means a different word — unless
 * your finger landed one key over. `hous` is a deletion; `horse` substitutes `r`
 * for `u`, which are nowhere near each other.
 *
 * That single rule also covers the slips the design asks for by name, with no
 * special cases: doubled letters (`hopping`/`hoping`) are an insertion, silent-e
 * drops are a deletion, and `ie`/`ei` (`recieve`/`receive`) is a transposition.
 *
 * A second guard catches what the first cannot: a longer word has a tolerance of
 * two, which is enough to turn `thinking` into `thanking`. So a near-miss that is
 * **itself a word the child has been taught** is always rejected. Accepting it
 * would mean marking a genuinely wrong answer right, which is a worse failure
 * than making a child retype.
 */
/**
 * Cost of substituting one letter for a letter that is **not** its keyboard
 * neighbour. Two, so it exceeds the tolerance for a short word on its own — this
 * is the number that rejects `horse` for `house`.
 */
const SUBSTITUTE_FAR = 2;
const SUBSTITUTE_NEAR = 1;
const INSERT_DELETE = 1;
const TRANSPOSE = 1;
/** Words up to this length get the tighter tolerance. */
const SHORT_WORD_LENGTH = 5;
const TOLERANCE_SHORT = 1;
const TOLERANCE_LONG = 2;
/**
 * The Danish keyboard, because that is the one under the child's hands.
 *
 * This is input tolerance, not pedagogy: [D-025](../../docs/DECISIONS.md#d-025)
 * refuses to *teach* through a Danish lens, and nothing here is ever shown to a
 * child. It only means a finger landing on `ø` instead of `l` is forgiven.
 */
const KEY_ROWS = ['qwertyuiopå', 'asdfghjklæø', 'zxcvbnm'];
/**
 * letter → the letters immediately around it, including diagonals.
 *
 * Only letters that are on the keyboard appear here. Digits and punctuation are
 * absent, so a substitution involving one is never treated as a slip — which is
 * right: nobody hits `2` while reaching for `e`.
 */
const NEIGHBOURS = (() => {
    const map = new Map();
    const add = (a, b) => {
        let set = map.get(a);
        if (!set) {
            set = new Set();
            map.set(a, set);
        }
        set.add(b);
    };
    KEY_ROWS.forEach((row, r) => {
        for (let c = 0; c < row.length; c++) {
            const key = row[c];
            for (const [dr, dc] of [
                [0, -1],
                [0, 1],
                [-1, 0],
                [-1, 1],
                [1, 0],
                [1, -1],
            ]) {
                const other = KEY_ROWS[r + dr]?.[c + dc];
                if (other !== undefined)
                    add(key, other);
            }
        }
    });
    return map;
})();
const areNeighbours = (a, b) => NEIGHBOURS.get(a)?.has(b) ?? false;
/**
 * Fold an answer to the form comparisons happen in.
 *
 * Case, surrounding space, repeated space and punctuation all carry no meaning
 * here — a child who typed "House!" has answered the question. Danish letters
 * fold to their two-letter spellings so `æble` and `aeble` are the same answer,
 * which is a real thing children type when the keyboard fights them.
 *
 * Swedish *ä* and *ö* fold to the same two letters as Danish *æ* and *ø*
 * ([D-190](../../../docs/DECISIONS.md#d-190)): a Danish keyboard has no *ä* or
 * *ö*, and a child who types *hjærta* for *hjärta* pressed the right key on the
 * keyboard they have. *här* and *har* stay two different answers.
 */
export function normalise(input) {
    return input
        .normalize('NFC')
        .toLowerCase()
        .replace(/æ/g, 'ae')
        .replace(/ø/g, 'oe')
        .replace(/ä/g, 'ae')
        .replace(/ö/g, 'oe')
        .replace(/å/g, 'aa')
        .replace(/[’']/g, "'")
        .replace(/[^\p{L}\p{N}'\s-]/gu, '')
        .replace(/\s+/g, ' ')
        .trim();
}
/**
 * Optimal string alignment distance, with substitution priced by keyboard
 * distance. Transpositions of adjacent characters count once, which is what
 * makes `recieve` a one-cost slip rather than two.
 */
export function editDistance(a, b) {
    const rows = a.length + 1;
    const cols = b.length + 1;
    const d = Array.from({ length: rows }, () => new Array(cols).fill(0));
    for (let i = 0; i < rows; i++)
        d[i][0] = i * INSERT_DELETE;
    for (let j = 0; j < cols; j++)
        d[0][j] = j * INSERT_DELETE;
    for (let i = 1; i < rows; i++) {
        for (let j = 1; j < cols; j++) {
            const ca = a[i - 1];
            const cb = b[j - 1];
            const substitution = ca === cb ? 0 : areNeighbours(ca, cb) ? SUBSTITUTE_NEAR : SUBSTITUTE_FAR;
            let best = Math.min(d[i - 1][j] + INSERT_DELETE, d[i][j - 1] + INSERT_DELETE, d[i - 1][j - 1] + substitution);
            if (i > 1 &&
                j > 1 &&
                ca === b[j - 2] &&
                a[i - 2] === cb) {
                best = Math.min(best, d[i - 2][j - 2] + TRANSPOSE);
            }
            d[i][j] = best;
        }
    }
    return d[a.length][b.length];
}
/** How far off an answer of this length is still a slip rather than a mistake. */
export const toleranceFor = (word) => word.length <= SHORT_WORD_LENGTH ? TOLERANCE_SHORT : TOLERANCE_LONG;
const miss = (distance) => ({
    outcome: 'incorrect',
    matched: null,
    exact: false,
    correction: null,
    distance,
});
/**
 * Score a typed answer.
 *
 * A near-miss is returned as **correct**, not as a partial: the design says such
 * an answer is "accepted with a warm correction, never marked wrong"
 * (LESSON-TYPES.md), and scoring it as half-right would quietly drag the child's
 * difficulty band down for spelling wobbles. `exact` and `correction` carry the
 * information a caller needs to show the correction and to count how often it
 * happens, without the child paying for it.
 */
export function scoreTyped(typed, options) {
    const answer = normalise(typed);
    const accepted = options.accepted.map(normalise).filter((a) => a.length > 0);
    if (accepted.length === 0 || answer.length === 0)
        return miss(Infinity);
    for (const target of accepted) {
        if (answer === target) {
            return {
                outcome: 'correct',
                matched: target,
                exact: true,
                correction: null,
                distance: 0,
            };
        }
    }
    // A near-miss that is itself a word this child knows is a wrong answer.
    const known = new Set();
    for (const w of options.knownWords ?? [])
        known.add(normalise(w));
    let best = null;
    for (const target of accepted) {
        const distance = editDistance(answer, target);
        if (best === null || distance < best.distance)
            best = { target, distance };
    }
    const nearest = best;
    const tolerance = options.maxEditDistance ?? toleranceFor(nearest.target);
    if (nearest.distance > tolerance)
        return miss(nearest.distance);
    if (known.has(answer))
        return miss(nearest.distance);
    return {
        outcome: 'correct',
        matched: nearest.target,
        exact: false,
        correction: nearest.target,
        distance: nearest.distance,
    };
}
/**
 * Score a built sentence against every ordering that reads correctly.
 *
 * Word banks routinely have more than one right answer — *"I would like a room"*
 * and *"I'd like a room"* are both fine — so the exercise carries a list rather
 * than one string.
 */
export function scoreWordBank(built, acceptedOrders) {
    const answer = built.map(normalise).filter((w) => w.length > 0);
    for (const order of acceptedOrders) {
        const target = order.map(normalise).filter((w) => w.length > 0);
        if (target.length === answer.length &&
            target.every((word, i) => word === answer[i])) {
            return {
                outcome: 'correct',
                matched: target.join(' '),
                exact: true,
                correction: null,
                distance: 0,
            };
        }
    }
    return miss(Infinity);
}
/**
 * Score a tap-one-of-several answer.
 *
 * Trivial, and deliberately shares the result shape with the others so a caller
 * never has to branch on exercise type to find out whether the child was right.
 */
export function scoreChoice(chosen, correct) {
    const a = normalise(chosen);
    const b = normalise(correct);
    return a === b
        ? { outcome: 'correct', matched: b, exact: true, correction: null, distance: 0 }
        : miss(Infinity);
}
//# sourceMappingURL=scoring.js.map